import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { FileText } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { parsePaymentTerms, calculateInvoiceAmount } from "@/lib/invoiceUtils";

interface CreateInvoicesButtonProps {
  leadId: string;
  paymentTerms: string | null;
  quotedPrice: number | null;
  currentStatus: string;
}

export const CreateInvoicesButton = ({
  leadId,
  paymentTerms,
  quotedPrice,
  currentStatus,
}: CreateInvoicesButtonProps) => {
  const [showDialog, setShowDialog] = useState(false);
  const queryClient = useQueryClient();
  const { data: quoteTerms } = useQuery({
    queryKey: ["accepted-quote-terms", leadId],
    enabled: !paymentTerms,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("quotations")
        .select("terms_and_conditions, total_amount")
        .eq("lead_id", leadId)
        .eq("status", "accepted")
        .order("accepted_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      return data as { terms_and_conditions: string | null; total_amount: number | null } | null;
    },
  });
  const effectiveTerms = paymentTerms || quoteTerms?.terms_and_conditions || null;
  const effectivePrice = quotedPrice || quoteTerms?.total_amount || null;

  const createInvoicesMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      if (!effectivePrice) {
        throw new Error("No quoted price available");
      }

      // Parse payment terms
      const installments = parsePaymentTerms(effectiveTerms);
      
      if (installments.length === 0) {
        throw new Error("Could not parse payment terms. Please check the format.");
      }

      // Create invoice records
      const invoices = installments.map((installment, index) => ({
        lead_id: leadId,
        user_id: user.id,
        installment_number: index + 1,
        percentage: installment.percentage,
        amount: calculateInvoiceAmount(installment.percentage, effectivePrice),
        due_date: installment.dueDate,
        status: "pending" as const,
      }));

      const { error: invoiceError } = await supabase
        .from("invoices")
        .insert(invoices);

      if (invoiceError) throw invoiceError;

      // Update lead status to invoicing
      const { error: leadError } = await supabase
        .from("leads")
        .update({ status: "invoicing" })
        .eq("id", leadId);

      if (leadError) throw leadError;

      // Log activity
      await supabase.from("activity_log").insert({
        lead_id: leadId,
        user_id: user.id,
        action: "Invoices Created",
        details: `Created ${installments.length} invoice installments from payment terms`,
      });

      // Log status change
      await supabase.from("lead_changes").insert({
        lead_id: leadId,
        user_id: user.id,
        field_changed: "status",
        old_value: currentStatus,
        new_value: "invoicing",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
      queryClient.invalidateQueries({ queryKey: ["invoices", leadId] });
      queryClient.invalidateQueries({ queryKey: ["activity-log", leadId] });
      queryClient.invalidateQueries({ queryKey: ["lead-changes", leadId] });
      toast.success("Invoices created successfully!");
      setShowDialog(false);
    },
    onError: (error: any) => {
      toast.error(`Failed to create invoices: ${error.message}`);
    },
  });

  // Only show button if status is quote_accepted
  if (currentStatus !== "quote_accepted") return null;

  const installments = parsePaymentTerms(effectiveTerms);
  const hasValidTerms = installments.length > 0;

  return (
    <>
      <Button
        onClick={() => setShowDialog(true)}
        className="gap-2"
        variant="default"
        disabled={!hasValidTerms || !effectivePrice}
      >
        <FileText className="h-4 w-4" />
        Create Invoices
      </Button>

      <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Create Invoice Installments?</AlertDialogTitle>
            <AlertDialogDescription>
              This will create {installments.length} invoice installment{installments.length > 1 ? 's' : ''} based on the payment terms:
              <ul className="mt-2 list-disc list-inside space-y-1">
                {installments.map((inst, idx) => (
                  <li key={idx}>
                    {inst.percentage}% {inst.dueDate ? `- Due ${inst.dueDate}` : ''}
                  </li>
                ))}
              </ul>
              <p className="mt-3">
                The lead status will be updated to "Invoicing" and you can then manage each invoice individually.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => createInvoicesMutation.mutate()}
              disabled={createInvoicesMutation.isPending}
            >
              {createInvoicesMutation.isPending ? "Creating..." : "Create Invoices"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
